import { useExpertUniversityStore } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { CreateTrainingSessionRequest, TrainingSession, UpdateTrainingSessionRequest } from "../types";
import type { TranscriptContent } from "./evaluations/types";
import { getTrainingSessionApiClient } from "./trainingSessionApi";

const logger = getLogger({ module: "Expert University API" });

interface TrainingSessionApiResult {
    count: number;
    items: TrainingSession[];
}

interface EvaluationByIdApiResult {
    count: number;
    items: TranscriptContent[];
}
const { sessionId: sessionIdFromStore } = useExpertUniversityStore.getState();
export const createTrainingSession = async (request: CreateTrainingSessionRequest) => {
    try {
        logger.info({ ...request, sessionId: request.SessionID }, "Creating training session");
        trackFullStoryEvent("Training Session Created", { sessionId: request.SessionID });
        await getTrainingSessionApiClient().post("create", {
            json: request,
        });
    } catch (error: any) {
        logger.info({ error: error.message, sessionId: request.SessionID }, "Error creating training session");
        trackFullStoryEvent("Training Session Creation Failed", { sessionId: request.SessionID });
    }
};

export const updateTrainingSession = async (request: UpdateTrainingSessionRequest) => {
    const sessionId = request.SessionID;
    try {
        logger.info({ ...request, sessionId }, "Updating training session");
        trackFullStoryEvent("Training Session Updated", { sessionId });
        await getTrainingSessionApiClient().post("update", {
            json: request,
        });
    } catch (error: any) {
        logger.info({ error: error.message, sessionId }, "Error updating training session");
        trackFullStoryEvent("Training Session Update Failed", { sessionId });
    }
};

export const getTrainingSessionsByEmployeeId = async (
    employeeId: string | null,
    challengeId?: string,
    sessionId?: string,
): Promise<TrainingSessionApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;
    try {
        logger.info({ employeeId, taskId }, "Getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions", { employeeId, taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                EmployeeId: employeeId,
                ChallengeId: challengeId,
                SessionId: sessionId,
            },
        });
        const trainingSessions = await response.json<TrainingSessionApiResult>();

        return trainingSessions;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions Failed", { employeeId, taskId });
    }
};

export const getEvaluationResultsBySessionId = async (
    sessionId: string,
): Promise<EvaluationByIdApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;

    try {
        logger.info({ taskId }, "Getting evaluation results by session ID");
        trackFullStoryEvent("Fetching Evaluation Results", { taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                SessionId: sessionId,
            },
        });
        const trainingSession = await response.json<EvaluationByIdApiResult>();
        return trainingSession;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting evaluation results by session ID");
        trackFullStoryEvent("Fetching Evaluation Results Failed", { taskId });
    }
};
